import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import {
  getPricingCurrency,
  getPropertyCardTagName,
  getPropertyCardTagStyles,
  isAvailable,
} from "../../helper";
import styles from "./PropertyTags.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const PropertyTags = ({
  className,
  isTenancyAvailable = true,
  inventory,
  dataTestId,
  isBookFormEnabledForInactiveProperty = false,
  showCashbackValue = false,
  hideCashbackValue = false,
  showCashbackIfOtherPropertyTagsDontHave = false,
  isSalesOfferAvailable = false,
}: {
  className?: string;
  isTenancyAvailable?: boolean;
  inventory: any;
  dataTestId?: string;
  isBookFormEnabledForInactiveProperty?: boolean;
  showCashbackValue?: boolean;
  hideCashbackValue?: boolean;
  showCashbackIfOtherPropertyTagsDontHave?: boolean;
  isSalesOfferAvailable?: boolean;
}) => {
  const { t } = useTranslationHook();
  const isInventoryAvailable =
    isTenancyAvailable && (isAvailable(inventory) || isBookFormEnabledForInactiveProperty);
  const meta = inventory?.meta ?? inventory?.meta_short;

  const showCashbackTag = !hideCashbackValue;
  const showRatings = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_top_rated === true || meta?.cro_tags?.is_top_rated === "true"),
  );
  const isFillingFast = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_fast_filling === true || meta?.cro_tags?.is_fast_filling === "true"),
  ); // we donot use this
  const isFillingFastV2 = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_filling_fast_v2 === true ||
        meta?.cro_tags?.is_filling_fast_v2 === "true"),
  );
  const isCashbackOrDiscount = Boolean(
    isInventoryAvailable && meta?.cro_tags?.cashback && showCashbackTag,
  );
  const isPropertyOfTheDay = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_property_of_the_day === true ||
        meta?.cro_tags?.is_property_of_the_day === "true"),
  );
  const isAmberExclusive = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_amber_exclusive === true ||
        meta?.cro_tags?.is_amber_exclusive === "true"),
  );
  const isStudentChoice = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_student_choice === true || meta?.cro_tags?.is_student_choice === "true"),
  );
  const isImmediateMoveIn = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_immediate_move_in === true ||
        meta?.cro_tags?.is_immediate_move_in === "true"),
  );

  const isBreakfastIncluded = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_breakfast_included === true ||
        meta?.cro_tags?.is_breakfast_included === "true"),
  );
  const isOnCampusInventory = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.on_campus === true || meta?.cro_tags?.on_campus === "true"),
  );
  const isOffCampusInventory = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.off_campus === true || meta?.cro_tags?.off_campus === "true"),
  );
  const isUniversityProvided = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_university_provided === true ||
        meta?.cro_tags?.is_university_provided === "true"),
  );
  const isUalHall = Boolean(
    isInventoryAvailable &&
      (meta?.cro_tags?.is_ual_hall === true || meta?.cro_tags?.is_ual_hall === "true"),
  );
  const cashbackValue = `${getPricingCurrency(inventory?.pricing?.currency)}${
    meta?.cro_tags?.cashback
  }`;
  let propertyCardTagText = null;
  const cashbackText =
    isCashbackOrDiscount && cashbackValue
      ? `${t("propertypage.components.pricetrends.saveUpTo")} ${cashbackValue}`
      : null;
  if (showCashbackValue && !showCashbackIfOtherPropertyTagsDontHave) {
    propertyCardTagText = cashbackText;
  } else {
    propertyCardTagText = getPropertyCardTagName({
      isTopRated: showRatings,
      isFillingFast,
      isFillingFastV2,
      isCashbackOrDiscount,
      cashbackValue,
      isPropertyOfTheDay,
      isAmberExclusive,
      isStudentChoice,
      isImmediateMoveIn,
      isBreakfastIncluded,
      isOnCampusInventory,
      isOffCampusInventory,
      isUniversityProvided,
      isUalHall,
      t,
    });
  }

  if (showCashbackIfOtherPropertyTagsDontHave) {
    if (propertyCardTagText === cashbackText) propertyCardTagText = null;
    else {
      propertyCardTagText = cashbackText;
    }
  }
  const propertyCardTagStyles: any = getPropertyCardTagStyles(propertyCardTagText);

  return (
    <>
      {!isInventoryAvailable && !showCashbackValue && (
        <div
          className={formatClassNames(
            styles.soldOutTag,
            isSalesOfferAvailable ? styles.soldOutTagWithSalesOffer : "",
            className,
          )}
          data-testid={dataTestId}
        >
          {t("searchpage.components.searchlistitem.propertytags.soldout")}
        </div>
      )}
      {isInventoryAvailable && propertyCardTagText && (
        <div
          className={formatClassNames(
            styles.soldOutTag,
            isSalesOfferAvailable ? styles.soldOutTagWithSalesOffer : "",
            className,
          )}
          style={{
            background: propertyCardTagStyles?.background,
            color: propertyCardTagStyles?.color,
            boxShadow: propertyCardTagStyles?.boxShadow,
          }}
          data-testid="property-tag"
        >
          <div
            className={
              propertyCardTagText === "Property of the Day" ||
              propertyCardTagText === "University Provided" ||
              propertyCardTagText === "UAL Hall"
                ? styles.gradientText
                : styles.text
            }
          >
            {t(propertyCardTagText, undefined, { dynamic: true })}
          </div>
        </div>
      )}
    </>
  );
};

export default wrapperHOC(PropertyTags, {
  componentName: "PropertyTags-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
